import { action } from '@ember/object';
import Component from '@glimmer/component';

import { isBlackOrWhiteColor } from 'webapp/utils/color';

import type { HtmlEditorOptions } from 'webapp/components/form/html-editor';
import FormHtmlEditorComponent from 'webapp/components/form/html-editor';

interface Args {
	text: string;
	onChange: (text: string) => void;
	onFocusOut?: (text: string) => void;
	options?: HtmlEditorOptions;
	placeholder?: string;
	static?: boolean;
	pasteAsPlainText?: boolean;
}

export default class FormHtmlEditorFullComponent extends Component<Args> {
	placeholder = this.args.placeholder || 'Click to add text...';

	get options(): HtmlEditorOptions {
		// Define the styles we want to keep (excluding color-related ones)
		const validStyles = {
			'*': [
				'font-size',
				'font-family',
				'font-weight',
				'font-style',
				'text-decoration',
				'text-align',
				'margin',
				'padding',
				'border',
				'list-style-type',
				'color',
				'width',
				'background-color',
				'border-width',
				'border-style',
				'border-color',
			],
		};

		const isDistractionFree = !!this.args.options?.isDistractionFree;

		return Object.assign(
			{
				placeholder: this.placeholder,
				toolbar: isDistractionFree
					? false
					: 'fontselect styleselect | forecolor bold italic underline | alignleft aligncenter alignright | bullist numlist | table emoticons removeformat link hr',
				table_toolbar:
					'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
				quickbars_selection_toolbar: isDistractionFree
					? 'fontselect styleselect | forecolor bold italic underline | alignleft aligncenter alignright | bullist numlist | outdent indent | removeformat link'
					: false,
				quickbars_insert_toolbar: false,
				plugins: [
					'link',
					'autolink',
					'lists',
					'paste',
					'wordcount',
					'noneditable',
					'emoticons',
					'table',
					'hr',
					'quickbars',
				],
				contextmenu: false,
				// paste plugin
				paste_block_drop: true,
				smart_paste: !this.args.pasteAsPlainText,
				paste_as_text: this.args.pasteAsPlainText ?? false,
				paste_remove_styles_if_webkit: this.args.pasteAsPlainText ?? false,
				valid_styles: this.args.pasteAsPlainText ? {} : validStyles,
				// link plugin
				default_link_target: '_blank',
				link_assume_external_targets: 'https',
				link_default_protocol: 'https',
				link_title: false,
				target_list: false,
				// table plugin
				table_default_styles: {
					width: '100%',
				},
				table_sizing_mode: 'relative',
				table_responsive_width: true,
				table_style_by_css: true,
				table_appearance_options: true,
				table_advtab: true,
				paste_preprocess: (_plugin: unknown, args: { content: string }) => {
					// always remove background-color styles
					args.content = args.content.replace(/background-color:\s*[^;"]+;?/gi, '');

					// remove color styles only if they are black or white
					// we only remove black or white colors to avoid issues when user unknowingly copies and pastes a black or white color into the editor
					// if he than changes theme the text "disappears"
					args.content = args.content.replace(
						/color:\s*([^;"]+);?/gi,
						(match, colorValue: string) => {
							return isBlackOrWhiteColor(colorValue) ? '' : match;
						},
					);
				},
				commandPopupList: this.args.options?.commandPopupList,
			},
			this.args.options || {},
		);
	}

	@action
	onChange(text: string): void {
		if (text !== this.args.text && text !== this.placeholder) {
			this.args.onChange(text);
		}
	}

	@action
	onFocusOut(text: string): void {
		// check the callback first: reading `@text` after the caller's model is gone throws
		const onFocusOut = this.args.onFocusOut;
		if (onFocusOut && text !== this.args.text && text !== this.placeholder) {
			onFocusOut(text);
		}
	}
	<template>
		<FormHtmlEditorComponent
			@text={{@text}}
			@onChange={{this.onChange}}
			@onFocusOut={{this.onFocusOut}}
			@options={{this.options}}
			@maxChars={{5000}}
			@static={{@static}}
		/>
	</template>
}
